/**
 * HALO - Employee Detail Page
 * Deep dive into individual employee wellness metrics.
 * Surfaces ALL JSONB detail fields: posture_details, fatigue_details,
 * hand_activity_details, and attention_metrics.
 */

import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Activity,
  Eye,
  Hand,
  Brain,
  AlertTriangle,
  Clock,
  Smile,
  Timer,
  Target,
  Gauge,
  TrendingUp,
  RefreshCw,
} from "lucide-react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useEmployeeDetail } from "@/hooks/useEmployeeDetail";

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function MetricGauge({
  label,
  value,
  max = 100,
  unit = "",
  color,
}: {
  label: string;
  value: number;
  max?: number;
  unit?: string;
  color: string;
}) {
  const percent = Math.min((value / max) * 100, 100);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-slate font-medium">{label}</span>
        <span className="font-mono font-semibold text-foreground">
          {typeof value === "number"
            ? Number.isInteger(value)
              ? value
              : value.toFixed(1)
            : value}
          <span className="text-[10px] text-muted-slate ml-0.5">{unit}</span>
        </span>
      </div>
      <div className="h-1.5 w-full bg-surface-elevated/50 rounded-full overflow-hidden">
        <motion.div
          className="h-full rounded-full"
          style={{ backgroundColor: color }}
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  subtext,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtext?: string;
  color: string;
}) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-surface-elevated/50">
      <div
        className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ backgroundColor: `${color}15` }}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs text-muted-slate">{label}</p>
        <p className="text-sm font-semibold text-foreground tabular-nums">
          {value}
        </p>
        {subtext && (
          <p className="text-[10px] text-muted-slate/70">{subtext}</p>
        )}
      </div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-surface-dark border border-glass-grey rounded-xl p-3 shadow-lg text-xs">
      <p className="text-muted-slate mb-2 font-medium">{label}</p>
      {payload.map((entry: any, idx: number) => (
        <div key={idx} className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-slate">{entry.name}</span>
          </div>
          <span className="text-foreground font-mono font-medium">
            {entry.name === "Fatigue"
              ? `${Math.round(entry.value * 100)}%`
              : `${entry.value}%`}
          </span>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface EmployeeDetailProps {
  organizationId: string;
  userId: string;
}

export default function EmployeeDetail(_props: EmployeeDetailProps) {
  const { employeeId } = useParams<{ employeeId: string }>();
  const navigate = useNavigate();

  const {
    profile,
    latestLog,
    logs,
    alerts,
    sessions,
    trendData,
    isLoading,
    error,
    refresh,
    days,
    setDays,
  } = useEmployeeDetail(employeeId || "");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-slate">Loading employee data...</p>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-signal-amber mx-auto mb-3" />
          <p className="text-sm text-foreground mb-2">
            {error || "Employee not found"}
          </p>
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-primary hover:underline"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  // Computed current values from latest log
  const current = latestLog;
  const wellnessScore = current
    ? Math.round(
        (current.posture_score || 0) * 0.4 +
          (1 - (current.fatigue_index || 0)) * 100 * 0.3 +
          ({
            Focused: 90,
            Happy: 95,
            Engaged: 85,
            Neutral: 70,
            Confused: 50,
            Tired: 40,
            Stressed: 35,
            Frustrated: 30,
          }[current.emotion_state] || 50) *
            0.3,
      )
    : 0;

  const timeRanges = [
    { label: "1d", days: 1 },
    { label: "7d", days: 7 },
    { label: "30d", days: 30 },
  ];

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-xl hover:bg-surface-elevated transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-muted-slate" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-foreground">
              {profile.full_name}
            </h1>
            <p className="text-sm text-muted-slate flex items-center gap-2">
              {profile.department && (
                <Badge variant="outline" className="text-[10px]">
                  {profile.department}
                </Badge>
              )}
              <span>{profile.email}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Time range selector */}
          <div className="flex items-center rounded-xl border border-glass-grey overflow-hidden">
            {timeRanges.map((range) => (
              <button
                key={range.days}
                onClick={() => setDays(range.days)}
                className={cn(
                  "px-3 py-1.5 text-xs font-medium transition-colors",
                  days === range.days
                    ? "bg-primary text-white"
                    : "text-muted-slate hover:text-foreground hover:bg-surface-elevated",
                )}
              >
                {range.label}
              </button>
            ))}
          </div>
          <button
            onClick={refresh}
            className="p-2 rounded-xl hover:bg-surface-elevated transition-colors"
          >
            <RefreshCw className="w-4 h-4 text-muted-slate" />
          </button>
        </div>
      </div>

      {/* Wellness Score Banner */}
      {current && (
        <motion.div
          className="flex items-center gap-6 p-4 rounded-2xl bg-gradient-to-r from-primary/10 via-primary/5 to-transparent border border-primary/20"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="text-center">
            <p className="text-3xl font-bold text-primary tabular-nums">
              {wellnessScore}%
            </p>
            <p className="text-xs text-muted-slate">Wellness Score</p>
          </div>
          <div className="h-10 w-px bg-glass-grey" />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 flex-1">
            <StatCard
              icon={<Smile className="w-4 h-4 text-signal-blue" />}
              label="Emotion"
              value={current.emotion_state}
              subtext={`${Math.round(
                current.emotion_confidence * 100,
              )}% confidence`}
              color="oklch(0.65 0.11 230)"
            />
            <StatCard
              icon={<Activity className="w-4 h-4 text-signal-green" />}
              label="Posture"
              value={`${current.posture_score}%`}
              color="oklch(0.70 0.11 150)"
            />
            <StatCard
              icon={<Brain className="w-4 h-4 text-signal-amber" />}
              label="Fatigue"
              value={`${Math.round(current.fatigue_index * 100)}%`}
              color="oklch(0.72 0.09 75)"
            />
            <StatCard
              icon={<Hand className="w-4 h-4 text-primary" />}
              label="Activity"
              value={current.hand_activity}
              color="oklch(0.55 0.12 280)"
            />
          </div>
        </motion.div>
      )}

      {/* Detail Panels Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Posture Details — JSONB data */}
        {current && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Activity className="w-4 h-4 text-signal-green" />
                Posture Details
              </CardTitle>
              <CardDescription>Detailed posture breakdown</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <MetricGauge
                label="Overall Posture Score"
                value={current.posture_score}
                color="oklch(0.70 0.11 150)"
                unit="%"
              />
              <MetricGauge
                label="Shoulder Alignment"
                value={current.posture_details?.shoulder_alignment || 0}
                color="oklch(0.65 0.11 230)"
                unit="%"
              />
              <MetricGauge
                label="Spine Curvature"
                value={current.posture_details?.spine_curvature || 0}
                color="oklch(0.60 0.10 280)"
                unit="%"
              />
              <div className="flex items-center justify-between text-xs pt-1">
                <span className="text-muted-slate">Head Tilt Angle</span>
                <span className="font-mono font-medium text-foreground">
                  {(current.posture_details?.head_tilt_angle || 0).toFixed(1)}°
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Fatigue Details — JSONB data */}
        {current && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Brain className="w-4 h-4 text-signal-amber" />
                Fatigue Details
              </CardTitle>
              <CardDescription>Eye and fatigue indicators</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <MetricGauge
                label="Fatigue Index"
                value={Math.round(current.fatigue_index * 100)}
                color="oklch(0.72 0.09 75)"
                unit="%"
              />
              <div className="grid grid-cols-2 gap-3 pt-1">
                <StatCard
                  icon={<Eye className="w-3.5 h-3.5 text-signal-blue" />}
                  label="Blink Rate"
                  value={`${
                    current.fatigue_details?.blink_rate_per_minute || 0
                  }/min`}
                  color="oklch(0.65 0.11 230)"
                />
                <StatCard
                  icon={<Timer className="w-3.5 h-3.5 text-signal-amber" />}
                  label="Avg Blink Duration"
                  value={`${
                    current.fatigue_details?.average_blink_duration_ms || 0
                  }ms`}
                  color="oklch(0.72 0.09 75)"
                />
                <StatCard
                  icon={
                    <AlertTriangle className="w-3.5 h-3.5 text-signal-crimson" />
                  }
                  label="Prolonged Closures"
                  value={(
                    current.fatigue_details?.prolonged_closures_count || 0
                  ).toString()}
                  color="oklch(0.55 0.14 25)"
                />
                <StatCard
                  icon={<Smile className="w-3.5 h-3.5 text-signal-green" />}
                  label="Yawn Detected"
                  value={current.fatigue_details?.yawn_detected ? "Yes" : "No"}
                  color={
                    current.fatigue_details?.yawn_detected
                      ? "oklch(0.72 0.09 75)"
                      : "oklch(0.70 0.11 150)"
                  }
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Attention Metrics — JSONB data */}
        {current && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                Attention Metrics
              </CardTitle>
              <CardDescription>Eye tracking and focus data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <MetricGauge
                label="Eye Contact"
                value={current.attention_metrics?.eye_contact_percent || 0}
                color="oklch(0.55 0.12 280)"
                unit="%"
              />
              <MetricGauge
                label="Gaze Stability"
                value={current.attention_metrics?.gaze_stability_score || 0}
                color="oklch(0.65 0.11 230)"
                unit="%"
              />
              <div className="grid grid-cols-2 gap-3 pt-1">
                <StatCard
                  icon={<Eye className="w-3.5 h-3.5 text-primary" />}
                  label="Gaze Direction"
                  value={current.attention_metrics?.gaze_direction || "—"}
                  color="oklch(0.55 0.12 280)"
                />
                <StatCard
                  icon={<Clock className="w-3.5 h-3.5 text-signal-green" />}
                  label="Focus Duration"
                  value={`${
                    current.attention_metrics?.screen_focus_duration_sec || 0
                  }s`}
                  color="oklch(0.70 0.11 150)"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Hand Activity Details — JSONB data */}
        {current && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Hand className="w-4 h-4 text-signal-blue" />
                Hand Activity Details
              </CardTitle>
              <CardDescription>
                Hand tracking and activity analysis
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-slate">Current Activity</span>
                  <Badge variant="outline">{current.hand_activity}</Badge>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <StatCard
                    icon={<Hand className="w-3.5 h-3.5 text-signal-blue" />}
                    label="Left Hand"
                    value={
                      current.hand_activity_details?.left_hand_detected
                        ? "Detected"
                        : "Not detected"
                    }
                    color="oklch(0.65 0.11 230)"
                  />
                  <StatCard
                    icon={<Hand className="w-3.5 h-3.5 text-signal-blue" />}
                    label="Right Hand"
                    value={
                      current.hand_activity_details?.right_hand_detected
                        ? "Detected"
                        : "Not detected"
                    }
                    color="oklch(0.65 0.11 230)"
                  />
                  <StatCard
                    icon={<Gauge className="w-3.5 h-3.5 text-signal-amber" />}
                    label="Finger Velocity"
                    value={`${(
                      current.hand_activity_details?.finger_velocity || 0
                    ).toFixed(1)}`}
                    color="oklch(0.72 0.09 75)"
                  />
                  <StatCard
                    icon={<Target className="w-3.5 h-3.5 text-primary" />}
                    label="Hand Zone"
                    value={
                      current.hand_activity_details?.hand_position_zone || "—"
                    }
                    color="oklch(0.55 0.12 280)"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Trend Chart */}
      {trendData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              Wellness Trend
            </CardTitle>
            <CardDescription>
              {days === 1 ? "Today" : `Last ${days} days`} — posture, fatigue,
              and eye contact
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[240px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={trendData}
                  margin={{ top: 5, right: 5, bottom: 5, left: 0 }}
                >
                  <defs>
                    <linearGradient
                      id="empFillPosture"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="oklch(0.70 0.11 150)"
                        stopOpacity={0.4}
                      />
                      <stop
                        offset="95%"
                        stopColor="oklch(0.70 0.11 150)"
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                    <linearGradient id="empFillEye" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor="oklch(0.55 0.12 280)"
                        stopOpacity={0.4}
                      />
                      <stop
                        offset="95%"
                        stopColor="oklch(0.55 0.12 280)"
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    vertical={false}
                    stroke="oklch(0.28 0 0)"
                    strokeDasharray="3 3"
                  />
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={{ fill: "oklch(0.45 0 0)", fontSize: 11 }}
                    tickFormatter={(v) =>
                      new Date(v).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                      })
                    }
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tick={{ fill: "oklch(0.45 0 0)", fontSize: 11 }}
                    domain={[0, 100]}
                  />
                  <RechartsTooltip content={<ChartTooltip />} />
                  <Area
                    name="Posture"
                    dataKey="posture"
                    type="monotone"
                    fill="url(#empFillPosture)"
                    stroke="oklch(0.70 0.11 150)"
                    strokeWidth={2}
                  />
                  <Area
                    name="Fatigue"
                    dataKey="fatigue"
                    type="monotone"
                    fill="none"
                    stroke="oklch(0.72 0.09 75)"
                    strokeWidth={2}
                    strokeDasharray="4 2"
                  />
                  <Area
                    name="Eye Contact"
                    dataKey="eyeContact"
                    type="monotone"
                    fill="url(#empFillEye)"
                    stroke="oklch(0.55 0.12 280)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center gap-6 mt-3 px-2">
              <div className="flex items-center gap-2 text-xs text-muted-slate">
                <div className="w-3 h-0.5 rounded bg-signal-green" /> Posture
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-slate">
                <div
                  className="w-3 h-0.5 rounded bg-signal-amber"
                  style={{ borderBottom: "1px dashed" }}
                />{" "}
                Fatigue
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-slate">
                <div
                  className="w-3 h-0.5 rounded"
                  style={{ backgroundColor: "var(--chart-eye-contact)" }}
                />{" "}
                Eye Contact
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sessions + Alerts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Session History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="w-4 h-4 text-signal-blue" />
              Session History
            </CardTitle>
            <CardDescription>
              {sessions.length} session{sessions.length !== 1 ? "s" : ""} in
              this period
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <p className="text-xs text-muted-slate text-center py-6">
                No sessions recorded in this period
              </p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {sessions.slice(0, 10).map((session) => (
                  <div
                    key={session.sessionId}
                    className="flex items-center justify-between p-3 rounded-xl bg-surface-elevated/50 hover:bg-surface-elevated transition-colors"
                  >
                    <div>
                      <p className="text-xs font-medium text-foreground">
                        {new Date(session.startTime).toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                          },
                        )}{" "}
                        {new Date(session.startTime).toLocaleTimeString(
                          "en-US",
                          {
                            hour: "numeric",
                            minute: "2-digit",
                          },
                        )}
                      </p>
                      <p className="text-[10px] text-muted-slate">
                        {session.durationMinutes}min · {session.dominantEmotion}{" "}
                        · {session.logCount} logs
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span
                        className="font-mono tabular-nums"
                        style={{
                          color:
                            session.avgWellness === null
                              ? "var(--muted-slate)"
                              : session.avgWellness >= 70
                              ? "oklch(0.70 0.11 150)"
                              : session.avgWellness >= 50
                              ? "oklch(0.72 0.09 75)"
                              : "oklch(0.55 0.14 25)",
                        }}
                      >
                        {session.avgWellness !== null
                          ? `${session.avgWellness}%`
                          : "—"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Alert History */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-signal-amber" />
              Alert History
            </CardTitle>
            <CardDescription>
              {alerts.length} alert{alerts.length !== 1 ? "s" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {alerts.length === 0 ? (
              <p className="text-xs text-muted-slate text-center py-6">
                No alerts for this employee
              </p>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {alerts.slice(0, 15).map((alert) => (
                  <div
                    key={alert.id}
                    className="flex items-start gap-3 p-3 rounded-xl bg-surface-elevated/50"
                  >
                    <div
                      className={cn(
                        "w-2 h-2 rounded-full mt-1.5 shrink-0",
                        alert.severity === "critical"
                          ? "bg-signal-crimson"
                          : alert.severity === "warning"
                          ? "bg-signal-amber"
                          : "bg-signal-blue",
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-xs text-foreground">{alert.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge
                          variant={
                            alert.severity === "critical"
                              ? "destructive"
                              : "outline"
                          }
                          className="text-[9px]"
                        >
                          {alert.alert_type.replace(/_/g, " ")}
                        </Badge>
                        <span className="text-[10px] text-muted-slate">
                          {new Date(alert.created_at).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              hour: "numeric",
                              minute: "2-digit",
                            },
                          )}
                        </span>
                        {alert.acknowledged && (
                          <Badge
                            variant="outline"
                            className="text-[9px] text-signal-green"
                          >
                            Acknowledged
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Processing Performance (from processing_time_ms) */}
      {current && (
        <div className="text-[10px] text-muted-slate/50 text-right px-2">
          Last detection processed in {current.processing_time_ms}ms ·{" "}
          {logs.length} data points loaded
        </div>
      )}
    </div>
  );
}
